import * as d3 from "d3-new";
import {createTooltip, moveTotal, translate,wrap} from "@/shared/charts/utils";
import {formatCompact, formatNumber} from "@/shared/utils/formatters";
import i18n from "@/lang/lang";

export const generateHorizontalStackedBarChart = (
  data,
  colors,
  config,
  customGroupSorting,
  translateSubGroup = false
) => {
  var { width, height, axisLabelSpacePercentage } = config;
  var axisLabelSpace = width * ((axisLabelSpacePercentage ?? 0) / 100);

  var series = calculateSeries(data);
  var { sortedGroups, x, y } = calculateXY(series, config, customGroupSorting);

  // Create the SVG container.
  const svg = d3.create("svg").attr("width", width).attr("height", height);

  var maxValue = series.reduce(
    (previousValue, currentValue) => previousValue + currentValue.total,
    0
  );

  // Append background rectangles
  svg
    .append("g")
    .selectAll()
    .data(sortedGroups)
    .join("rect")
    .attr("x", axisLabelSpace)
    .attr("y", (d) => y(d) ?? 0)
    .attr("width", width - axisLabelSpace)
    .attr("height", y.bandwidth())
    .attr("fill", "#F2F6F8")
    .attr("rx", 5) // Rounded corners
    .attr("ry", 5); // Rounded corners

  // Append a group for each series, and a rect for each element in the series.
  var chart = svg
    .append("g")
    .selectAll()
    .data(series.filter((series) => series.total))
    .join("g")
    .selectAll("path")
    .data((D) => D.stacks.filter((stack) => stack.value))
    .join("path")
    .attr("fill", (d) => d.color ?? colors[d.subGroup])
    .attr("d", (d, i) => {
      var radiusLeft = 0;
      var radiusRight = 0;
      //if first subgroup, round the corners of the stack
      if (d.first) {
        radiusLeft = 5;
      }
      if (d.last) {
        radiusRight = 5;
      }
      var barLength =
        x(d.value) - radiusLeft - radiusRight - axisLabelSpace + 1;
      if (barLength < 0) {
        barLength = 0;
      }
      return `
        M${x(d.bottom) + radiusLeft},${y(`${d.group}`)}
        h${barLength}
        a${radiusRight},${radiusRight} 0 0 1 ${radiusRight},${radiusRight}
        v${y.bandwidth() - radiusRight * 2}
        a${radiusRight},${radiusRight} 0 0 1 ${-radiusRight},${radiusRight}
        h${-barLength}
        a${radiusLeft},${radiusLeft} 0 0 1 ${-radiusLeft},${-radiusLeft}
        v${-(y.bandwidth() - radiusLeft * 2)}
        a${radiusLeft},${radiusLeft} 0 0 1 ${radiusLeft},${-radiusLeft}
        Z`;
    });

  createTooltip(chart, "horizontal-stacked-bar-chart-tooltip", (d) => {
    return `${
      translateSubGroup ? i18n.t(d.subGroup) : d.subGroup
    } ${formatNumber(d.value)} (${d.percentage})`;
  });

  var separation = 10;

  var totals = svg
    .append("g")
    .selectAll("text")
    .data(series)
    .join("text")
    .attr("x", (d) => x(d.total) + separation)
    .attr("y", (d) => (y(d.key) ?? 0) + y.bandwidth() / 2)
    .attr("dy", "0.35em")
    .attr("font-size", config.labelsFontSize ?? "16px")
    .attr("class", "horizontal-stacked-bar-chart-percentage")
    .style("opacity", 0)
    .text((d) => {
      return `${formatCompact(d.total)} (${d.percentage})`;
    })
    .call(moveTotal, x(maxValue), separation);

  chart
    .style("transform-origin", `${axisLabelSpace}px`)
    .style("transform", "scaleX(0)")
    .transition()
    .duration(500)
    .style("transform", "scaleX(1)")
    .on("end", () => {
      totals.transition().duration(300).style("opacity", 1);
    });

  // Rounded corners

  //        Append the vertical axis.
  svg
    .append("g")
    .attr("transform", `translate(${axisLabelSpace},0)`)
    .attr("class", "horizontal-stacked-bar-chart-axis")
    .call(d3.axisLeft(y))
    .call((g) => g.selectAll(".domain, .tick line").remove())
    .selectAll("text")
    .attr("font-size", config.axisLabelFontSize ?? "12px")
    .call(translate)
    //  .call(wrapAndFit, axisLabelSpace - 5)
    .call(wrap, axisLabelSpace - 5);

  // Append text labels for totals

  // Transition when loading

  return svg.node();
};

const calculateSeries = (data) => {
  var stacks = {};
  var grandTotal = 0;
  data.forEach(function (d) {
    if (!d.value) {
      return;
    }
    if (typeof stacks[d.group] === "undefined") {
      stacks[d.group] = {
        key: d.group,
        bottom: 0,
        total: 0,
        stacks: [],
        percentage: "",
      };
    }
    stacks[d.group].stacks.push({
      ...d,
      bottom: stacks[d.group].bottom,
      first: !stacks[d.group].stacks.length,
      last: true,
      total: 0,
      percentage: "",
    });

    if (stacks[d.group].stacks.length > 1) {
      stacks[d.group].stacks[stacks[d.group].stacks.length - 2].last = false;
    }

    stacks[d.group].bottom = stacks[d.group].bottom + d.value;
    stacks[d.group].total += d.value;
    grandTotal += d.value;
  });
  var stacksArray = Object.values(stacks);
  stacksArray.forEach((d) => {
    d.percentage =
      ((grandTotal != 0 ? d.total / grandTotal : 0) * 100).toFixed(0) + "%";
    d.stacks.forEach((s) => {
      s.percentage =
        d.stacks.length > 1
          ? ((d.total != 0 ? s.value / d.total : 0) * 100).toFixed(0) + "%"
          : d.percentage;
    });
  });
  return Object.values(stacks);
};

const calculateXY = (data, config, customGroupSorting) => {
  var { width, height, marginTop, axisLabelSpacePercentage, marginBottom } =
    config;

  var axisLabelSpace = width * ((axisLabelSpacePercentage ?? 0) / 100);

  var sortedGroups = customGroupSorting ?? data.map((d) => d.key);
  //  Prepare the scales for positional and color encodings.
  const y = d3
    .scaleBand()
    .domain(sortedGroups)
    .range([marginTop, height - marginBottom])
    .padding(0.13);

  var maxValue = data.reduce(
    (previousValue, currentValue) => previousValue + currentValue.total,
    0
  );
  var xAxisRange = [0, maxValue];

  const x = d3
    .scaleLinear()
    .domain(xAxisRange)
    .rangeRound([axisLabelSpace, width]);
  return {
    sortedGroups,
    x,
    y,
  };
};
